/**
 * V2 — single-flight platform refresh: POST `/api/v1/customer-auth/refresh`.
 * Cookie-only credentials + CSRF; cross-tab exclusive lock.
 */

import type { RuntimeConfig } from '../../config/runtimeConfig.js';
import { isHttpStormLatched } from '../http/httpStormDetectedLatch.js';
import { stormAwareFetch } from '../http/stormAwareFetch.js';
import { customerAuthCsrfHeaders } from './customerAuthCsrf.js';
import {
  REFRESH_LOCK_TIMEOUT_CODE,
  RefreshLockTimeoutError,
  withCrossTabRefreshLock,
} from './crossTabRefreshLock.js';
import { isCustomerSessionEstablished } from './customerSessionEstablishedLatch.js';
import { isSessionEndingLatchActive } from './customerSessionEndingLatch.js';
import { isMissingCredentialUnauthorized } from './fetchPlatformMe.js';
import { isLoginLandInFlight } from './loginLandGate.js';

/** Wire timeout for POST `/customer-auth/refresh` — mirrors `/me` AbortController guard. */
export const PLATFORM_REFRESH_WIRE_TIMEOUT_MS = 10_000;

/** Paths that must not trigger refresh-on-401 (identity + step-up boundary). */
export const CUSTOMER_REFRESH_401_DENY_SUBSTRINGS = [
  '/customer-auth/refresh',
  '/logout',
  '/logout-all',
  '/request-otp',
  '/verify-otp',
  '/login-password',
  '/register',
  '/set-credentials',
  '/link-account',
  '/oidc/',
  '/request-step-up',
  '/verify-step-up',
  '/customer-account/me',
] as const;

export type SessionRefreshResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly status: number; readonly code?: string };

export type CustomerUnauthorizedAction = 'retry' | 'end' | 'none';

/** Admin parity (H1): refresh family dead — end session immediately, not soft-stop. */
const HARD_REVOCATION_CODES = new Set([
  'INVALID_REFRESH_TOKEN',
  /** Refresh cookie absent/expired — BE already cleared cookies; callApi:false on end. */
  'REFRESH_TOKEN_MISSING',
  'SESSION_REVOKED',
  'ACCOUNT_LOCKED',
]);

export function shouldAttemptSessionRefreshOn401(path: string): boolean {
  return !CUSTOMER_REFRESH_401_DENY_SUBSTRINGS.some((substring) =>
    path.includes(substring),
  );
}

/** @deprecated ApiClient twin — prefer {@link shouldAttemptSessionRefreshOn401}. */
export function shouldAttemptRefreshOn401(path: string): boolean {
  return shouldAttemptSessionRefreshOn401(path);
}

export function isHardSessionRefreshRevocation(code: string | undefined): boolean {
  return code !== undefined && HARD_REVOCATION_CODES.has(code);
}

/**
 * Whether a missing-credential 401 should still attempt POST …/customer-auth/refresh.
 * Anonymous cold boot / guest / mid-boot / login land / session-ending → false.
 * Established cookie SPA (latch set from CSP hydrate/establish) → true.
 * Cookie V2: BAN memory Bearer / tokenStorage gating (admin may use Bearer OR latch).
 */
export function shouldAttemptRefreshOnMissingCredential(): boolean {
  if (isLoginLandInFlight()) {
    return false;
  }
  if (isSessionEndingLatchActive()) {
    return false;
  }
  return isCustomerSessionEstablished();
}

let inFlightRefresh: Promise<SessionRefreshResult> | null = null;

export function resetSessionRefreshForTests(): void {
  inFlightRefresh = null;
}

async function postPlatformRefresh(config: RuntimeConfig): Promise<SessionRefreshResult> {
  if (isHttpStormLatched()) {
    return { ok: false, status: 429, code: 'HTTP_STORM_LATCHED' };
  }

  const base = config.apiBaseUrl.replace(/\/+$/, '');
  const url = `${base}/api/v1/customer-auth/refresh`;
  const idempotencyKey =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => {
    timeoutController.abort();
  }, PLATFORM_REFRESH_WIRE_TIMEOUT_MS);

  let response: Response;
  try {
    response = await stormAwareFetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
        ...customerAuthCsrfHeaders(),
      },
      body: JSON.stringify({}),
      signal: timeoutController.signal,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'HttpStormLatchedError') {
      return { ok: false, status: 429, code: 'HTTP_STORM_LATCHED' };
    }
    if (
      (err instanceof DOMException && err.name === 'AbortError') ||
      (err instanceof Error && err.name === 'AbortError')
    ) {
      return { ok: false, status: 0, code: 'NETWORK_ERROR' };
    }
    if (err instanceof TypeError) {
      return { ok: false, status: 0, code: 'NETWORK_ERROR' };
    }
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
  }

  type RefreshJson = { success?: boolean; code?: string; error?: string; message?: string };
  let json: RefreshJson | null = null;
  let code: string | undefined;
  let message: string | undefined;
  try {
    json = (await response.json()) as RefreshJson;
    code = json.code;
    message =
      (typeof json.error === 'string' ? json.error : undefined) ??
      (typeof json.message === 'string' ? json.message : undefined);
  } catch {
    // non-JSON
  }

  if (response.ok && json?.success === true) {
    return { ok: true };
  }

  if (response.status === 401 || response.status === 403) {
    if (isMissingCredentialUnauthorized(code, message)) {
      return { ok: false, status: response.status, code: 'MISSING_CRED' };
    }
  }

  return { ok: false, status: response.status, code };
}

/** Single-flight refresh under cross-tab lock. */
export async function attemptSessionRefresh(
  config: RuntimeConfig,
): Promise<SessionRefreshResult> {
  // G6 — login land unlock window: refuse concurrent refresh until endLoginLand.
  if (isLoginLandInFlight()) {
    return { ok: false, status: 401, code: 'LOGIN_LAND_IN_FLIGHT' };
  }
  if (inFlightRefresh !== null) {
    return inFlightRefresh;
  }

  const promise = (async (): Promise<SessionRefreshResult> => {
    try {
      return await withCrossTabRefreshLock(() => postPlatformRefresh(config));
    } catch (err) {
      if (err instanceof RefreshLockTimeoutError) {
        return { ok: false, status: 0, code: REFRESH_LOCK_TIMEOUT_CODE };
      }
      if (err instanceof TypeError) {
        return { ok: false, status: 0, code: 'NETWORK_ERROR' };
      }
      throw err;
    }
  })().finally(() => {
    if (inFlightRefresh === promise) {
      inFlightRefresh = null;
    }
  });

  inFlightRefresh = promise;
  return promise;
}

/** ApiClient 401 recovery — refresh once, retry original request, or end session. */
export async function handleUnauthorizedForPath(
  config: RuntimeConfig,
  path: string,
  code?: string,
  message?: string,
): Promise<CustomerUnauthorizedAction> {
  // G6 — same window as attemptSessionRefresh: no refresh-on-401 during land.
  if (isLoginLandInFlight()) {
    return 'none';
  }
  if (!shouldAttemptSessionRefreshOn401(path)) {
    return 'none';
  }
  // Spec A dying-access: anonymous missing-cred stays silent; established cookie
  // SPA still rotates (access cookie expiry while refresh cookie may live).
  if (
    isMissingCredentialUnauthorized(code, message) &&
    !shouldAttemptRefreshOnMissingCredential()
  ) {
    return 'none';
  }

  const refreshed = await attemptSessionRefresh(config);
  if (refreshed.ok) {
    return 'retry';
  }
  if (isHardSessionRefreshRevocation(refreshed.code)) {
    return 'end';
  }
  return 'none';
}

/** Manual refresh entry (authApi refresh hook). */
export async function fetchManualSessionRefreshCoordinated(args: {
  config: RuntimeConfig;
}): Promise<SessionRefreshResult> {
  return attemptSessionRefresh(args.config);
}
